import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Zap, Swords, Wand2, Trophy, CheckCircle2, XCircle, Gamepad2, Award,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TABS = [
  { id: "feel", label: "Game Feel", icon: Zap },
  { id: "gold", label: "Gold Quality Gate", icon: Trophy },
];

export default function GameFeelStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("feel");
  const [targets, setTargets] = useState(null);
  const [meta, setMeta] = useState(null);

  const refreshMeta = useCallback(() => { api.get(`/visual/${gameId}`).then((r) => setMeta(r.data)).catch(() => {}); }, [gameId]);
  useEffect(() => { api.get("/visual/quality-targets").then((r) => setTargets(r.data)).catch(() => {}); refreshMeta(); }, [gameId, refreshMeta]);

  return (
    <div className="min-h-screen bg-[#070912] text-white" data-testid="game-feel-studio">
      <div className="max-w-3xl w-full mx-auto p-5 lg:p-6">
        <div className="flex items-center gap-3 mb-1.5">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="feel-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-orange-400 text-black"><Gamepad2 className="w-5 h-5" /></span>
          <div>
            <h1 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight leading-none">Game Feel &amp; Polish</h1>
            <p className="text-[11px] text-white/40 mt-0.5">Make it feel alive — punchy impact, weighty combat, and a Gold-ready release check.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 my-4" data-testid="feel-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`feel-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${tab === t.id ? "bg-white text-black border-white" : "bg-white/5 text-white/70 border-white/12 hover:bg-white/10"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "feel" && <FeelTab gameId={gameId} targets={targets} meta={meta} onChange={refreshMeta} />}
        {tab === "gold" && <GoldTab gameId={gameId} />}
      </div>
    </div>
  );
}

function Section({ children }) {
  return <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:p-5">{children}</div>;
}

function FeelTab({ gameId, targets, meta, onChange }) {
  const feels = targets?.feel_presets || [];
  const combats = targets?.combat_feels || [];
  const weapons = targets?.weapon_feels || [];
  const cur = meta?.feel || {};
  const [saving, setSaving] = useState(null);
  const [applying, setApplying] = useState(null);

  const curId = feels.find((f) => cur.shake != null && Math.abs((f.patch?.shake ?? -9) - cur.shake) < 0.001)?.id;

  const setFeel = async (id) => {
    setSaving(id);
    try { const { data } = await api.post(`/visual/${gameId}/feel/preset`, { preset: id }); toast.success(`Game Feel → ${data.label}. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set feel."); }
    finally { setSaving(null); }
  };
  const applyFeel = async (kind, id) => {
    setApplying(`${kind}-${id}`);
    try {
      const { data } = await api.post(`/visual/${gameId}/feel/apply`, { kind, id }, { timeout: 150000 });
      if (data.changed) toast.success(`${data.label} — saved v${data.version} (undoable).`);
      else toast.message(data.summary || "No safe change could be applied.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply."); }
    finally { setApplying(null); }
  };

  return (
    <div className="space-y-4" data-testid="feel-panel">
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Impact intensity</p>
        <p className="text-xs text-white/50 mb-3">Screen shake &amp; hit-stop on every hit. Lower it for calm/accessible play, crank it for cinematic punch.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="feel-presets">
          {feels.map((f) => (
            <button key={f.id} onClick={() => setFeel(f.id)} disabled={saving} data-testid={`feel-preset-${f.id}`}
              className={`text-left rounded-xl border p-2.5 transition-colors ${curId === f.id ? "border-rose-400/60 bg-rose-400/[0.08]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"} disabled:opacity-50`}>
              <div className="flex items-center gap-1.5"><span className="text-lg">{f.emoji}</span><span className="text-sm font-bold text-white/90">{f.label}</span>{saving === f.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-300 ml-auto" />}</div>
              <p className="text-[10px] text-white/45 mt-0.5">{f.desc}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1 flex items-center gap-1"><Swords className="w-3 h-3" /> Combat feel</p>
        <p className="text-xs text-white/50 mb-3">One tap re-tunes how fighting feels. Applied safely &amp; undoable.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="combat-feels">
          {combats.map((c) => (
            <button key={c.id} onClick={() => applyFeel("combat", c.id)} disabled={!!applying} data-testid={`combat-feel-${c.id}`}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 hover:bg-white/[0.07] transition-colors disabled:opacity-50">
              <span className="text-lg">{c.emoji}</span><span className="text-sm font-semibold text-white/90 flex-1 truncate">{c.label}</span>
              {applying === `combat-${c.id}` && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/60" />}
            </button>
          ))}
        </div>
      </Section>

      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1 flex items-center gap-1"><Wand2 className="w-3 h-3" /> Weapon feel</p>
        <p className="text-xs text-white/50 mb-3">Give the hero's weapon a distinct character.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="weapon-feels">
          {weapons.map((w) => (
            <button key={w.id} onClick={() => applyFeel("weapon", w.id)} disabled={!!applying} data-testid={`weapon-feel-${w.id}`}
              className="flex flex-col items-center gap-1 rounded-xl border border-white/10 bg-white/[0.03] px-2 py-2.5 hover:bg-white/[0.07] transition-colors disabled:opacity-50">
              <span className="text-xl">{w.emoji}</span><span className="text-xs font-semibold text-white/90">{w.label}</span>
              {applying === `weapon-${w.id}` && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/60" />}
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function GoldTab({ gameId }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [marking, setMarking] = useState(false);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/visual/${gameId}/gold/check`); setRes(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run the check."); }
    finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [gameId]);

  const mark = async () => {
    setMarking(true);
    try { const { data } = await api.post(`/visual/${gameId}/gold/mark`); setRes(data); toast.success("🏆 Marked GameView Gold Ready!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Not all checks pass yet."); }
    finally { setMarking(false); }
  };

  const groups = (res?.checks || []).reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="gold-panel">
      <Section>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Award className={`w-6 h-6 ${res?.gold_ready ? "text-amber-400" : "text-white/40"}`} />
            <div>
              <p className="text-sm font-bold text-white">{res ? `${res.passed} / ${res.total} checks passing` : "Checking…"}</p>
              <p className="text-[11px] text-white/45">A coordinated release-readiness review across gameplay, visuals, audio, feel & publishing.</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="gold-recheck-btn" className="rounded-full border-white/15 text-white/70 hover:bg-white/10">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-check"}
            </Button>
            <Button size="sm" onClick={mark} disabled={marking || !res?.gold_ready} data-testid="gold-mark-btn"
              className="rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-bold disabled:opacity-40">
              {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trophy className="w-4 h-4" /> Mark Gold Ready</>}
            </Button>
          </div>
        </div>
      </Section>

      {Object.entries(groups).map(([cat, items]) => (
        <Section key={cat}>
          <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">{cat}</p>
          <div className="space-y-1.5">
            {items.map((c) => (
              <div key={c.id} data-testid={`gold-check-${c.id}`} className="flex items-start gap-2.5">
                {c.pass ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />}
                <div>
                  <p className={`text-sm font-semibold ${c.pass ? "text-white/90" : "text-white/90"}`}>{c.label}</p>
                  <p className="text-[11px] text-white/45">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}
